import React, { useEffect, useState } from 'react';
import moment from 'moment';
import './AdsAnalyticsQueryBuilder.pcss';
import { CtmAnalyticsQuery } from '@mm/state/model/CtmAnalyticsModel';
import { AnalyticsFilterByDate } from '../../BotAnalytics/AnalyticsFilterByTime/AnalyticsFilterByDate';
import { SearchInput } from '../../../../../../../common/components/SearchInput/SearchInput';
import { Heading } from '@mm/components';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import AdAccountModel from '@mm/state/model/AdAccountModel';
import {
  generateBasicStructure,
  nameById,
} from '@mm/state/helpers/filterable-select-helpers';
import FilterableSelect from '../../../../../../../common/components/FilterableSelect/FilterableSelect';
import { NeedHelpLabel } from '../../../../../common/components/NeedHelpLabel/NeedHelpLabel';

type Props = {
  query: CtmAnalyticsQuery | null;
  adAccounts: AdAccountModel[];
  setQuery: (query: CtmAnalyticsQuery) => void;
};

export const AdsAnalyticsQueryBuilder = (props: Props) => {
  const { query, setQuery, adAccounts } = props;
  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);
  const [dateRangeMode, setDateRangeMode] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [activeSearch, setActiveSearch] = useState('');
  const [accountId, setAccountId] = useState<string | null>(null);

  useEffect(() => {
    if (!initialized && query !== null) {
      setAccountId(query.parentId);
      setStartDate(query.range.since);
      setEndDate(query.range.until);
      setActiveSearch(query.query || '');
      setInitialized(true);
    }
  }, [query === null]);

  if (!initialized) {
    return null;
  }

  return (
    <div className="ads-analytics-query-builder">
      <SearchInput
        className="ads-analytics-query-builder__search-input"
        placeholder="Search"
        value={activeSearch}
        onChange={e => setActiveSearch(e.target.value)}
      />
      <div className="ads-analytics-query-builder__date-heading-container">
        <Heading
          className="ads-analytics-query-builder__date-heading"
          text="View by date"
          size={4}
        />
        <NeedHelpLabel
          iconOnly={true}
          link="https://customers.ai/help/article/beo6r98ja4-analytics"
        />
      </div>
      <AnalyticsFilterByDate
        className="ads-analytics-query-builder__calendar"
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        dateRangeMode={dateRangeMode}
        showClearDates={false}
        setDateRangeMode={setDateRangeMode}
        presetsToExclude={['Last 6 Months']}
        instantChangeMode={true}
      />
      <Heading
        className="ads-analytics-query-builder__date-heading"
        text="View by ad account"
        size={4}
      />
      <FilterableSelect
        onChange={(id: string) => setAccountId(id)}
        outerValue={nameById(
          accountId || '',
          adAccounts.map(account => ({
            ...account,
            id: Number(account.id),
          })),
          'No Ad Account',
        )}
        className="ads-analytics-query-builder__ad-account-selector"
        suggestions={generateBasicStructure(adAccounts)}
        placeholder="Select Ad Account"
      />
      <PrimaryButton
        disabled={!query?.parentId}
        className="ads-analytics-query-builder__search-button"
        onClick={() => {
          if (query === null) {
            return;
          }
          const {
            range: { since, until },
            parentId,
          } = query;
          setQuery({
            token: null,
            parentId: accountId || parentId,
            query: activeSearch || null,
            range: { since: startDate || since, until: endDate || until },
          });
        }}
      >
        Apply
      </PrimaryButton>
    </div>
  );
};
